import { Button, DatePicker, Divider, Modal, Space, Table, TableProps, Tag } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../utils/useAuth";
import { useSelector } from "react-redux";
import { isAdmin } from "../../../utils/permissions";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { BASE_API_URL } from "../../../utils/consts";

type Props = {};

const LocationsSettings: React.FC<Props> = () => {
  const params = useParams();
  const { t } = useTranslation();
  const authUser: any = useAuth();
  const userData = useSelector((state: any) => state.userData.user);
  const [locations, setLocations] = useState<any[]>([]);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [freezeTarget, setFreezeTarget] = useState<any>(null);
  const [freezeDate, setFreezeDate] = useState<Dayjs | null>(dayjs());
  const navigate = useNavigate();

  const clientId =
    typeof userData?.clientId === "object"
      ? userData?.clientId?._id
      : userData?.clientId;

  const fetchLocations = () => {
    if (!clientId) return;
    axios
      .get(`${BASE_API_URL}/${clientId}/locations`)
      .then((res) => setLocations(res.data))
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    if (params.id !== "new") {
      fetchLocations();
    }
  }, [params.id, clientId]);

  const handleToggleFreeze = async (loc: any) => {
    if (!loc.frozen) {
      setFreezeDate(dayjs());
      setFreezeTarget(loc);
      return;
    }
    setTogglingId(loc._id);
    try {
      await axios.put(`${BASE_API_URL}/location/${loc._id}`, {
        frozen: false,
      });
      fetchLocations();
    } catch (err) {
      console.error(err);
    } finally {
      setTogglingId(null);
    }
  };

  const confirmFreeze = async () => {
    if (!freezeTarget || !freezeDate) return;
    setTogglingId(freezeTarget._id);
    try {
      await axios.put(`${BASE_API_URL}/location/${freezeTarget._id}`, {
        frozen: true,
        frozenAt: freezeDate.toISOString(),
      });
      setFreezeTarget(null);
      fetchLocations();
    } catch (err) {
      console.error(err);
    } finally {
      setTogglingId(null);
    }
  };

  const columns: TableProps<any>["columns"] = [
    {
      title: t("Location Name"),
      dataIndex: "name",
      key: "name",
      render: (name: string, record: any) => (
        <Space>
          {name}
          {record.frozen && (
            <Tag color="default" style={{ fontSize: 11 }}>
              {t("Frozen")}
              {record.frozenAt ? ` (${dayjs(record.frozenAt).format("DD.MM.YYYY.")})` : ""}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t("Action"),
      key: "action",
      render: (row: any) => (
        <Space size="middle">
          <Button onClick={() => navigate(`/settings/location/${row._id}`)}>
            {t("View")}
          </Button>
          {isAdmin(authUser?.roles) && (
            <Button
              onClick={() => handleToggleFreeze(row)}
              loading={togglingId === row._id}
            >
              {row.frozen ? t("Unfreeze") : t("Freeze")}
            </Button>
          )}
          <Button
            onClick={() => {
              axios
                .delete(`${BASE_API_URL}/location/${row._id}/delete`)
                .then(() => fetchLocations())
                .catch((err) => console.error(err));
            }}
          >
            {t("Delete")}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <>
        {isAdmin(authUser?.roles) && (
          <Button
            type="primary"
            onClick={() => navigate("/settings/location/new")}
          >
            {t("Add Location")}
          </Button>
        )}
        <Divider />
        <Table columns={columns} dataSource={locations} rowKey="_id" />
      </>
      <Modal
        title={t("Freeze location")}
        open={!!freezeTarget}
        onOk={confirmFreeze}
        onCancel={() => setFreezeTarget(null)}
        confirmLoading={togglingId === freezeTarget?._id}
        okText={t("Freeze")}
      >
        <p>{freezeTarget?.name}</p>
        <DatePicker
          value={freezeDate}
          onChange={setFreezeDate}
          format="DD.MM.YYYY."
          style={{ width: "100%" }}
        />
      </Modal>
    </>
  );
};

export default LocationsSettings;
